"use client"
import IconMinus from "@/app/(site)/_components/Atoms/Icons/IconMinus"
import IconPlus from "@/app/(site)/_components/Atoms/Icons/IconPlus"
import StoryText from "@/app/(site)/_components/Atoms/Storytext"
import ButtonText from "@/app/(site)/_components/Molecules/ButtonText"
import { getLangCode } from "@/app/(site)/_utils/misc"
import { t } from "@/translations"
import { usePathname } from "next/navigation"
import React, { useEffect, useRef, useState } from "react"

interface ReadMoreTextProps {
    htmlString: string
}

const ReadMoreText: React.FC<ReadMoreTextProps> = ({ htmlString }) => {
    const [showAll, setShowAll] = useState(true)
    const topOfComponentRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        setShowAll(true)
    }, [])

    const langCode = getLangCode(usePathname().split("/")[1]) || ""

    const handleReadMoreLessClick = () => {
        setShowAll(prev => !prev)
    }

    const addMarginToParagraphs = React.useCallback((html: string): JSX.Element[] => {
        // Split the html string into paragraphs
        // and map over them to create a new array of JSX elements
        // with the paragraphs wrapped in <p> tags
        // and the line breaks wrapped in <br> tags
        return html.split(/<\/?p>/).reduce<JSX.Element[]>((acc, paragraph, index) => {
            const paragraphsFromBr = paragraph.split("<br />").map((part, partIndex) => (
                <React.Fragment key={`${index}-${partIndex}`}>
                    {part}
                    {partIndex !== paragraph.split("<br />").length - 1 && <br />}
                </React.Fragment>
            ))
            if (paragraph.trim()) {
                acc.push(
                    <StoryText className="mb-32" size="size3" key={index}>
                        {paragraphsFromBr}
                    </StoryText>
                )
            }
            return acc
        }, [])
    }, [])

    const paragraphs = addMarginToParagraphs(htmlString)

    return (
        <div ref={topOfComponentRef}>
            <div
                className={`${!showAll && htmlString.length > 100 ? "line-clamp-2" : ""}`}
            >
                {paragraphs}
            </div>
            {htmlString.length > 100 && (
                <ButtonText
                    asChild
                    size="big"
                    icon={
                        showAll ? (
                            <IconMinus className="h-16 w-16" />
                        ) : (
                            <IconPlus className="h-16 w-16" />
                        )
                    }
                    iconPosition="right"
                    onClick={handleReadMoreLessClick}
                    className="flex items-center gap-4 p-14 pl-0"
                >
                    {showAll
                        ? t(langCode, "common.read_less")
                        : t(langCode, "common.read_more")}
                </ButtonText>
            )}
        </div>
    )
}

export default ReadMoreText
